import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Home, FileText, BarChart3, Building, Bell, Plus, LogOut, Calculator
} from 'lucide-react';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';

// Custom hook for click outside
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

const NotificationPanel = ({
  notifications, 
  allOrders, 
  onSelectOrder, 
  onClose, 
  onMarkAsRead, 
  onDelete, 
  onDeleteAll
}) => {
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const getNotificationIcon = (type, isRead) => {
    switch (type) {
      case 'delivery_3days':
        return <div className="mt-1 h-2 w-2 rounded-full bg-orange-500"></div>;
      case 'delivery_1day':
        return <div className="mt-1 h-2 w-2 rounded-full bg-yellow-500"></div>;
      case 'delivery_today':
        return <div className="mt-1 h-2 w-2 rounded-full bg-red-500"></div>;
      default:
        return <div className={`mt-1 h-2 w-2 rounded-full ${isRead ? 'bg-transparent' : 'bg-blue-500'}`}></div>;
    }
  };

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'delivery_3days':
        return 'border-l-4 border-l-orange-500 bg-orange-50';
      case 'delivery_1day':
        return 'border-l-4 border-l-yellow-500 bg-yellow-50';
      case 'delivery_today':
        return 'border-l-4 border-l-red-500 bg-red-50';
      default:
        return '';
    }
  };

  const handleNotificationClick = (notification) => {
    if (isSelectionMode) {
      const newSelected = new Set(selectedNotifications);
      if (newSelected.has(notification.id)) {
        newSelected.delete(notification.id);
      } else {
        newSelected.add(notification.id);
      }
      setSelectedNotifications(newSelected);
    } else if (notification.orderId) {
      const fullOrder = allOrders.find(order => order.id === notification.orderId);
      if (fullOrder) {
        onSelectOrder(fullOrder);
      } else {
        onSelectOrder({id: notification.orderId, status: notification.orderStatus});
      }
      onClose();
    }
  };

  const handleMarkAsRead = (notificationId, e) => {
    e.stopPropagation();
    onMarkAsRead(notificationId);
  };

  const handleDeleteSelected = () => {
    if (selectedNotifications.size > 0) {
      onDelete(Array.from(selectedNotifications));
      setSelectedNotifications(new Set());
      setIsSelectionMode(false);
    }
  };

  const handleDeleteAll = () => {
    if (window.confirm('Are you sure you want to delete all notifications?')) {
      onDeleteAll();
      setSelectedNotifications(new Set());
      setIsSelectionMode(false);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedNotifications(new Set());
  };

  const selectedCount = selectedNotifications.size;

  return (
    <div className="absolute top-full right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg z-20">
      <div className="p-3 border-b font-semibold flex justify-between items-center">
        <span>Notifications</span>
        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <>
              <button
                onClick={toggleSelectionMode}
                className={`p-1 rounded text-xs ${isSelectionMode ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title={isSelectionMode ? 'Exit Selection Mode' : 'Select Notifications'}
              >
                {isSelectionMode ? 'Exit' : 'Select'}
              </button>
              {isSelectionMode && (
                <>
                  <button
                    onClick={handleDeleteSelected}
                    disabled={selectedCount === 0}
                    className="p-1 rounded text-xs bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Delete ${selectedCount} selected`}
                  >
                    Delete ({selectedCount})
                  </button>
                  <button
                    onClick={handleDeleteAll}
                    className="p-1 rounded text-xs bg-red-100 text-red-700 hover:bg-red-200"
                    title="Delete all notifications"
                  >
                    Delete All
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {notifications.length > 0 ? notifications.map(n => (
          <div
            key={n.id}
            onClick={() => handleNotificationClick(n)}
            className={`p-3 border-b hover:bg-gray-50 flex items-start gap-3 ${
              !n.orderId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            } ${getNotificationStyle(n.type)} ${
              isSelectionMode && selectedNotifications.has(n.id) ? 'bg-blue-50 border-blue-200' : ''
            }`}
            title={!n.orderId ? "No order to view for this notification" : ""}
          >
            {isSelectionMode && (
              <input
                type="checkbox"
                checked={selectedNotifications.has(n.id)}
                onChange={() => {}}
                className="mt-1"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {getNotificationIcon(n.type, n.isRead)}
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-sm">{n.message}</p>
                  <p className="text-xs text-gray-500 truncate">{n.orderTitle}</p>
                  <p className="text-xs text-gray-400 mt-1">{n.createdAt?.toDate().toLocaleString('en-GB')}</p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {!n.isRead && (
                    <button
                      onClick={(e) => handleMarkAsRead(n.id, e)}
                      className="p-1 rounded text-xs bg-green-100 text-green-700 hover:bg-green-200"
                      title="Mark as read"
                    >
                      ✓
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )) : <p className="p-4 text-sm text-gray-500">No notifications.</p>}
      </div>
    </div>
  );
};

const Header = ({ 
  onNavigate, 
  user, 
  currentView, 
  onSignOut, 
  onSignIn, 
  msAccessToken, 
  allOrders 
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const unreadCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);
  const notificationsRef = useRef(null);
  
  useEffect(() => {
    if (!user) {
      return;
    }
    const q = query(collection(db, 'notifications'), where('recipientUid', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      notifs.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));
      setNotifications(notifs);
    }, (error) => {
      console.error('Error fetching notifications:', error);
    });
    return unsubscribe;
  }, [user]);

  useClickOutside(notificationsRef, () => setShowNotifications(false));

  const handleBellClick = async () => {
    if (!showNotifications && unreadCount > 0) {
      const batch = writeBatch(db);
      notifications.forEach(n => {
        if (!n.isRead) {
          const notifRef = doc(db, 'notifications', n.id);
          batch.update(notifRef, { isRead: true });
        }
      });
      await batch.commit();
    }
    setShowNotifications(!showNotifications);
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const notifRef = doc(db, 'notifications', notificationId);
      await updateDoc(notifRef, { isRead: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotifications = async (notificationIds) => {
    try {
      const batch = writeBatch(db);
      notificationIds.forEach(id => {
        const notifRef = doc(db, 'notifications', id);
        batch.delete(notifRef);
      });
      await batch.commit();
    } catch (error) {
      console.error('Error deleting notifications:', error);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      const batch = writeBatch(db);
      notifications.forEach(n => {
        const notifRef = doc(db, 'notifications', n.id);
        batch.delete(notifRef);
      });
      await batch.commit();
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  };

  return (
    <header className="sticky top-0 bg-white/80 backdrop-blur-md z-10 flex justify-between items-center p-4 border-b border-gray-200">
      <div className="flex items-center gap-4">
        <button onClick={() => onNavigate('list')} className="p-2 rounded-md hover:bg-gray-100 transition-colors">
          <Home size={20} />
        </button>
        <h1 className="text-xl font-bold">UrbanTech</h1>
      </div>
      <div className="flex items-center gap-4">
        {currentView !== 'proforma' && (
          <button
            onClick={() => onNavigate('proforma')}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors group relative"
            title="Proforma Invoices"
          >
            <FileText size={20} className="text-gray-600 group-hover:text-black" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              Proforma Invoices
            </span>
          </button>
        )}
        {currentView !== 'analytics' && (
          <button
            onClick={() => onNavigate('analytics')}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors group relative"
            title="Analytics"
          >
            <BarChart3 size={20} className="text-gray-600 group-hover:text-black" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              Analytics
            </span>
          </button>
        )}
        {currentView !== 'costSheet' && (
          <button
            onClick={() => onNavigate('costSheet')}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors group relative"
            title="Cost Sheet"
          >
            <Calculator size={20} className="text-gray-600 group-hover:text-black" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              Cost Sheet
            </span>
          </button>
        )}
        {(currentView === 'list' || currentView === 'create') && (
          <button 
            onClick={() => onNavigate('vendors')} 
            className="p-2 rounded-md hover:bg-gray-100 transition-colors group relative"
            title="Vendors"
          >
            <Building size={20} className="text-gray-600 group-hover:text-black" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              Vendors
            </span>
          </button>
        )}
        <div className="relative" ref={notificationsRef}>
          <button onClick={handleBellClick} className="p-2 rounded-full text-gray-600 hover:bg-gray-100 relative">
            <Bell size={24}/>
            {unreadCount > 0 && <span className="absolute top-1 right-1 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />}
          </button>
          {showNotifications && <NotificationPanel 
            notifications={notifications} 
            allOrders={allOrders}
            onSelectOrder={(order) => onNavigate('details', order)} 
            onClose={() => setShowNotifications(false)}
            onMarkAsRead={markNotificationAsRead}
            onDelete={deleteNotifications}
            onDeleteAll={deleteAllNotifications}
          />} 
        </div>

        {currentView === 'list' && (
          <button onClick={() => onNavigate('create')} className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800">
            <Plus size={18} />
            <span>Add Requirement</span>
          </button>
        )}
        <div className="text-right mt-5">
          <button onClick={onSignOut} className="flex items-center gap-2 text-sm bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700">
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
          {user && <p className="text-xs text-gray-500 mt-1">Signed in as {user.displayName}</p>}
        </div>
      </div>
    </header>
  );
};

export default Header;
